import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { SearchInput } from './ui/search-input'
import { Button } from './ui/button'
import { Card } from '@/components/ui/card'
import { Clock, MapPin, Plane, Plus, TriangleAlert } from '@/components/icons'
import { hapticLight } from '@/lib/haptics'
import { AircraftDrawer } from './AircraftDrawer'
import { useAircraftGetAll, useFlightLogAircraftStats } from '../lib/hooks'
import { formatWholeHours, formatWholeNumber } from '@/lib/flight-log-utils'
import { AircraftStatCard } from './AircraftStatCard'
import { useSubscriptionStatus } from '../lib/hooks/subscription'
import { AircraftStatusBadge } from './AircraftStatusBadge'
import { TopBarBackIconButton } from './topbar'
import { useIsAdmin } from '@/hooks/useIsCompanyAdmin'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState } from './EmptyState'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog'
import { AppLayout } from './AppLayout'
import { DesktopPage } from './desktop/DesktopPage'
import { DesktopPageHeader } from './desktop/DesktopPageHeader'
import { useIsDesktop } from './ui/use-mobile'

interface Aircraft {
  id: string
  tail: string
  type: string
  serialNumber: string
  base: string
  status: 'active' | 'maintenance' | 'inactive'
  nextFlight?: string
  model: string
}

interface AircraftListProps {
  onBack: () => void
}

export function AircraftList({ onBack }: AircraftListProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false)
  const [isLimitDialogOpen, setIsLimitDialogOpen] = useState(false)
  const { isAdmin } = useIsAdmin()

  // Include archived aircraft so they can be shown inline (greyed) and unarchived.
  const { data: aircraft = [], isPending: loading } = useAircraftGetAll({
    includeArchived: true,
  })
  const { data: subscription } = useSubscriptionStatus()
  const maxAircraft = subscription?.maxActiveAircraft ?? 3
  const isDesktop = useIsDesktop()

  const matchesSearch = (plane: (typeof aircraft)[number]) =>
    (plane.registration || '')
      .toLowerCase()
      .includes(searchQuery.toLowerCase()) ||
    (plane.type || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (plane.baseAirportIcao || '')
      .toLowerCase()
      .includes(searchQuery.toLowerCase())

  const fleetAircraft = aircraft.filter((a) => !a.isArchived)
  const archivedAircraft = aircraft.filter((a) => a.isArchived)

  // Non-archived first, archived last; both filtered by the search box.
  const visibleAircraft = [...fleetAircraft, ...archivedAircraft].filter(
    matchesSearch
  )

  // Stats and the add-limit count reflect the active fleet (non-archived) only.
  const totalAircraft = fleetAircraft.length
  const maintenanceAircraft = fleetAircraft.filter(
    (a) => a.aircraftStatus === 'Maintenance'
  ).length

  // Fleet flight hours / distance: per-aircraft totals from submitted flight
  // logs, summed over the active fleet only (archived aircraft excluded, like
  // the Total card).
  const { data: aircraftStats = [], isPending: statsLoading } =
    useFlightLogAircraftStats()
  const statsById = new Map(aircraftStats.map((s) => [s.aircraftId, s]))
  let fleetFlightMinutes = 0
  let fleetDistanceNm = 0
  for (const plane of fleetAircraft) {
    const stats = statsById.get(plane.id)
    if (!stats) continue
    fleetFlightMinutes += stats.totalFlightMinutes
    fleetDistanceNm += stats.totalDistanceNm
  }

  const handleAdd = () => {
    hapticLight()
    if (totalAircraft >= maxAircraft) {
      setIsLimitDialogOpen(true)
    } else {
      setIsAddDrawerOpen(true)
    }
  }

  const renderSkeletonList = () => (
    <div className="space-y-3 flex flex-col flex-1">
      {Array.from({ length: 5 }).map((_, i) => (
        <Card key={i}>
          <div className="flex items-center justify-between">
            <div className="flex gap-3 items-center">
              <Skeleton className="size-12 rounded-lg" />
              <div className="flex flex-col gap-2">
                <Skeleton className="h-5 w-24" />
                <Skeleton className="h-4 w-16" />
              </div>
            </div>
            <Skeleton className="h-6 w-16 rounded-full" />
          </div>
        </Card>
      ))}
    </div>
  )

  const renderAircraftList = (list: typeof aircraft) => (
    <div className="space-y-3 flex flex-col flex-1">
      {list.map((plane) => (
        <Link
          key={plane.id || plane.registration}
          to={isAdmin ? '/operations/aircraft/$aircraftId' : '/aircraft/$aircraftId'}
          params={{ aircraftId: plane.id } as any}
          className="block"
        >
          <Card className={plane.isArchived ? 'opacity-60' : undefined}>
            <div className="flex items-center justify-between">
              {/* Left side - Info */}
              <div className="flex gap-3 items-center">
                <div className="flex flex-col">
                  <h4
                    className={`font-semibold text-base ${
                      plane.isArchived
                        ? 'text-content-primary-subtle'
                        : 'text-content-primary-bold'
                    }`}
                  >
                    {plane.registration}
                  </h4>
                  <p className="text-content-primary-subtle text-sm">
                    {plane.type}
                  </p>
                </div>
              </div>

              {/* Right side - Archived marker or status badge */}
              {plane.isArchived ? (
                <span className="text-xs font-medium text-content-primary-subtle bg-surface-L2 rounded-full px-2.5 py-1">
                  Archived
                </span>
              ) : (
                <AircraftStatusBadge status={plane.aircraftStatus} />
              )}
            </div>
          </Card>
        </Link>
      ))}
    </div>
  )

  const body = (
    <>
      <div className="flex flex-col gap-4 flex-1">
        {/* Search Bar */}
        <SearchInput
          placeholder="Search for aircraft"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onClear={() => setSearchQuery('')}
        />

        {/* Stats — fleet counts, then fleet flight hours / miles */}
        {loading || statsLoading ? (
          <div className="flex flex-col gap-3">
            {[0, 1].map((row) => (
              <div key={row} className="flex gap-3">
                {[0, 1].map((i) => (
                  <Skeleton key={i} className="h-[92px] flex-1 rounded-xl" />
                ))}
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex gap-3">
              <AircraftStatCard
                label="Total"
                value={totalAircraft}
                icon={<Plane />}
                subtitle="Aircraft"
              />
              <AircraftStatCard
                label="Maintenance"
                value={maintenanceAircraft}
                icon={<TriangleAlert />}
                subtitle="Currently"
                borderClassName={
                  maintenanceAircraft > 0
                    ? 'border border-content-notice-bold'
                    : undefined
                }
                iconClassName={
                  maintenanceAircraft > 0
                    ? 'text-content-notice-bold'
                    : undefined
                }
              />
            </div>
            <div className="flex gap-3">
              <AircraftStatCard
                label="Flight Hours"
                value={formatWholeHours(fleetFlightMinutes)}
                icon={<Clock />}
                subtitle="Total for Fleet"
              />
              <AircraftStatCard
                label="Miles Flown"
                value={formatWholeNumber(fleetDistanceNm)}
                icon={<MapPin />}
                subtitle="Total for Fleet"
              />
            </div>
          </div>
        )}

        {/* Aircraft list — active fleet first, archived (greyed) last */}
        {loading ? (
          renderSkeletonList()
        ) : visibleAircraft.length === 0 ? (
          <EmptyState
            icon={Plane}
            title="No aircraft yet"
            description={
              isAdmin
                ? 'Add your first aircraft to start using Pronteer.'
                : 'No aircraft have been added yet.'
            }
            addButtonText={isAdmin ? 'Add Aircraft' : undefined}
            onAdd={isAdmin ? handleAdd : undefined}
          />
        ) : (
          renderAircraftList(visibleAircraft)
        )}
      </div>

      {/* Add Aircraft Drawer */}
      <AircraftDrawer
        isOpen={isAddDrawerOpen}
        onClose={() => setIsAddDrawerOpen(false)}
      />

      {/* Aircraft Limit Dialog */}
      <AlertDialog open={isLimitDialogOpen} onOpenChange={setIsLimitDialogOpen}>
        <AlertDialogContent className="bg-surface-L0">
          <AlertDialogHeader>
            <AlertDialogTitle>Aircraft Limit Reached</AlertDialogTitle>
            <AlertDialogDescription>
              You've reached the {maxAircraft}-aircraft limit of your plan.
              {'\n\n'}To add another aircraft, please remove an existing one,{' '}
              <Link
                to="/operations/subscriptions"
                className="text-content-brand-bold underline"
                onClick={() => setIsLimitDialogOpen(false)}
              >
                upgrade your plan
              </Link>
              , or contact{' '}
              <a
                href="mailto:support@pronteer.com"
                className="text-content-brand-bold underline"
              >
                support@pronteer.com
              </a>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              variant="primary"
              size="mini"
              className="w-full"
              onClick={() => setIsLimitDialogOpen(false)}
            >
              OK
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )

  const breadcrumb = (
    <span>
      <Link to="/operations" className="hover:underline">
        Operations
      </Link>
      <span className="text-content-primary-subtle"> / Aircraft</span>
    </span>
  )

  const addAction = isAdmin ? (
    <Button
      roundness="round"
      variant="brand"
      size="sm"
      className="gap-2"
      onClick={handleAdd}
    >
      Add Aircraft
      <Plus className="size-4 text-content-primary-white" />
    </Button>
  ) : undefined

  if (isDesktop) {
    return (
      <DesktopPage>
        <DesktopPageHeader
          title="Aircraft"
          breadcrumb={breadcrumb}
          action={addAction}
        />
        {body}
      </DesktopPage>
    )
  }

  return (
    <AppLayout
      topBar={
        <TopBarBackIconButton
          title="Aircraft"
          icon={
            isAdmin ? (
              <Button roundness="round" variant="brand" size="sm" className="size-8 p-0">
                <Plus className="size-4 text-content-primary-white" />
              </Button>
            ) : undefined
          }
          onIconClick={
            isAdmin
              ? () => {
                  hapticLight()
                  if (totalAircraft >= maxAircraft) {
                    setIsLimitDialogOpen(true)
                  } else {
                    setIsAddDrawerOpen(true)
                  }
                }
              : undefined
          }
          showBackButton={true}
        />
      }
      className="py-4"
    >
      {body}
    </AppLayout>
  )
}
